import type { PendingLegalAcceptance } from "@dropl/shared";
import { api } from "../api/index.js";
import { t } from "../i18n/index.js";
import { modal, toast } from "./overlay.js";
import { escapeHtml } from "./shell.js";

let running = false;

export const LEGAL_ACCEPTED_EVENT = "dropl:legal_accepted";

export async function enforceLegalAcceptance(): Promise<void> {
  if (running) return;
  running = true;
  try {
    let pending: PendingLegalAcceptance[] = [];
    try {
      const res = await api<{ items: PendingLegalAcceptance[] }>("/api/legal/pending");
      pending = res.items;
    } catch {
      return;
    }
    if (pending.length === 0) return;

    const accepted = await showBulkAcceptModal(pending);
    if (!accepted) {
      toast.warn(t("legal.gate.toast_refused"), { duration: 6000 });
      try {
        await api("/api/auth/logout", { method: "POST", body: {} });
      } catch {  }
      location.href = "/login";
      return;
    }

    window.dispatchEvent(new CustomEvent(LEGAL_ACCEPTED_EVENT, {
      detail: { versionIds: pending.map((p) => p.version.id) },
    }));
  } finally {
    running = false;
  }
}

function showBulkAcceptModal(pending: PendingLegalAcceptance[]): Promise<boolean> {
  const body = document.createElement("div");
  const latestDate = pending
    .map((p) => p.version.publishedAt ? new Date(p.version.publishedAt).getTime() : 0)
    .reduce((a, b) => Math.max(a, b), 0);
  const dateStr = latestDate ? new Date(latestDate).toLocaleDateString() : "—";

  body.innerHTML = `
    <p class="t-small" style="color: var(--fg-muted); margin: 0 0 var(--s-3);">
      ${escapeHtml(t("legal.gate.lead_bulk", { n: pending.length, date: dateStr }))}
    </p>
    <div class="legal-gate__docs">
      ${pending.map((p, i) => `
        <details class="legal-gate__doc" ${i === 0 ? "open" : ""}>
          <summary>
            <i class="fa-solid fa-file-contract" aria-hidden="true"></i>
            <span>${escapeHtml(p.version.pageTitle)}</span>
            <span class="badge badge--ink t-mono">v${p.version.versionNumber}</span>
          </summary>
          <div class="legal-gate__body legal-page__body">${p.version.bodyHtml}</div>
        </details>`).join("")}
    </div>
    <label class="landing-legal" style="margin-top: var(--s-3);">
      <input type="checkbox" data-bulk-consent />
      <span>${escapeHtml(t("legal.gate.bulk_consent", { n: pending.length }))}</span>
    </label>`;

  return new Promise<boolean>((resolve) => {
    let accepted = false;
    void modal.open({
      title: t("legal.gate.title"),
      icon: "fa-solid fa-gavel",
      modalClass: "dr-modal--wide",
      closeOnBackdrop: false,
      closeOnEscape: false,
      content: body,
      actions: [
        {
          label: t("legal.gate.refuse"),
          className: "btn btn--ghost",
          onClick: ({ close }) => { close(); },
        },
        {
          label: t("legal.gate.accept_all", { n: pending.length }),
          className: "btn btn--primary",
          primary: true,
          onClick: async ({ body: b, close }) => {
            const consent = b.querySelector<HTMLInputElement>("[data-bulk-consent]")?.checked ?? false;
            if (!consent) {
              toast.warn(t("legal.gate.must_consent"));
              return;
            }
            try {

              await Promise.all(
                pending.map((p) =>
                  api("/api/legal/accept", { method: "POST", body: { versionId: p.version.id } }),
                ),
              );
              accepted = true;
              close();
            } catch (err: unknown) {
              toast.error(err instanceof Error ? err.message : String(err));
            }
          },
        },
      ],
    }).then(() => resolve(accepted));
  });
}
